import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ComparisonTable from '../components/ComparisonTable';
import { parseLogicData, parseVibeData, parseUniverseData } from '../utils/parseComparisonData';

const TestCaseViewer = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestCase();
  }, [testId]);

  const renderSynthesis = (text) => {
    if (!text) return null;

    // Check for Option C by looking for the emoji marker
    const optionCMarker = '💡 Alternative Path Detected';
    const optionCStart = text.indexOf(optionCMarker);
    
    if (optionCStart !== -1) {
      // Find the end of Option C (before "Does that feel aligned")
      const afterMarker = text.substring(optionCStart);
      const endMarker = afterMarker.indexOf('Does that feel');
      const optionCEnd = endMarker !== -1 ? optionCStart + endMarker : text.length;
      
      // Split into before, Option C, and after
      const beforeOptionC = text.substring(0, optionCStart).trim();
      const optionCBox = text.substring(optionCStart, optionCEnd).trim();
      const afterOptionC = text.substring(optionCEnd).trim();
      
      // Parse Option C content (no need to clean box characters)
      const cleanContent = optionCBox;
      const lines = cleanContent.split('\n').filter(line => line.trim());
      
      let optionCName = '';
      let whatItIs = '';
      let whyItems = [];
      let solutionType = '';
      let closingNote = '';
      
      let currentSection = '';
      let inOptionC = false;
      
      lines.forEach(line => {
        const trimmed = line.trim();
        
        // Skip emoji line and empty lines
        if (trimmed.includes('Alternative Path Detected') || trimmed.includes('💡') || !trimmed) return;
        
        if (trimmed.startsWith('OPTION C:')) {
          optionCName = trimmed.replace('OPTION C:', '').trim();
          currentSection = 'name';
          inOptionC = true;
        } else if (trimmed === 'What it is:') {
          currentSection = 'what';
        } else if (trimmed === 'Why this might work:') {
          currentSection = 'why';
        } else if (trimmed.startsWith('This is a')) {
          solutionType = trimmed;
          currentSection = 'closing';
        } else if (currentSection === 'what' && trimmed) {
          whatItIs += (whatItIs ? ' ' : '') + trimmed;
        } else if (currentSection === 'why' && trimmed.startsWith('•')) {
          whyItems.push(trimmed.substring(1).trim());
        } else if (currentSection === 'closing' && trimmed && !trimmed.startsWith('This is a')) {
          closingNote += (closingNote ? ' ' : '') + trimmed;
        }
      });
      
      return (
        <>
          {/* Before Option C */}
          {beforeOptionC.split('\n\n').map((para, idx) => (
            <p key={`before-${idx}`}>{para}</p>
          ))}
          
          {/* Option C Box */}
          <div className="glass-card p-6 sm:p-8 my-6 border-l-4 border-purple-500">
            <div className="flex items-start gap-3 mb-6">
              <span className="text-3xl flex-shrink-0">💡</span>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-purple-900 mb-1">Alternative Path Detected</h3>
                <p className="text-purple-700 text-sm sm:text-base">A third way forward</p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Option C Name */}
              <div>
                <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {optionCName}
                </h4>
              </div>
              
              {/* What it is */}
              {whatItIs && (
                <div className="bg-white/50 rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">What it is:</h5>
                  <p className="text-gray-700 leading-relaxed">{whatItIs}</p>
                </div>
              )}
              
              {/* Why this might work */}
              {whyItems.length > 0 && (
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Why this might work:</h5>
                  <ul className="space-y-2">
                    {whyItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1 flex-shrink-0">✓</span>
                        <span className="text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Solution type */}
              {solutionType && (
                <div className="pt-4 border-t border-purple-200">
                  <p className="text-gray-700 font-semibold">{solutionType}</p>
                </div>
              )}
              
              {/* Closing note */}
              {closingNote && !closingNote.includes('Does that feel') && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-gray-700 italic leading-relaxed">{closingNote}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* After Option C */}
          {afterOptionC && (
            <p className="italic text-gray-600">{afterOptionC}</p>
          )}
        </>
      );
    }
    
    // No Option C - just render paragraphs
    return text.split('\n\n').map((para, idx) => (
      <p key={idx}>{para}</p>
    ));
  };

  const loadTestCase = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-cases/${testId}`);
      if (response.ok) {
        const data = await response.json();
        setDecision(data);
      }
    } catch (error) {
      console.error('Error loading test case:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading test case...</div>
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-xl text-red-600">Test case not found</div>
      </div>
    );
  }

  const logicParsed = parseLogicData(decision.logic_pros || [], decision.logic_cons || []);
  const universeParsed = parseUniverseData(decision.serendipity_input || '');
  const vibeParsed = parseVibeData(decision.vibe_input || '');

  // Match Universe and Vibe names to Logic names
  universeParsed.optionA.name = logicParsed.optionA.name;
  universeParsed.optionB.name = logicParsed.optionB.name;
  vibeParsed.optionA.name = logicParsed.optionA.name;
  vibeParsed.optionB.name = logicParsed.optionB.name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/test-cases')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to test cases list
          </button>
        </div>

        {/* Title Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">{decision.title}</h1>
            <span className="px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
              EXPECTED OUTPUT
            </span>
          </div>
        </div>

        {/* Original Prompt */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Original Prompt</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{decision.original_prompt}</p>
        </div>

        {/* Logic Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">Logic</h2>
          </div>
          <p className="text-gray-600 text-sm mb-6">
            The practical considerations. Facts, consequences, and rational thinking.
          </p>
          <ComparisonTable
            optionA={logicParsed.optionA.name}
            optionB={logicParsed.optionB.name}
            itemsA={logicParsed.optionA.items}
            itemsB={logicParsed.optionB.items}
            iconA={decision.option_a_icon}
            iconB={decision.option_b_icon}
          />
        </div>

        {/* Universe Section */}
        {decision.serendipity_input && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Universe</h2>
            </div>
            <p className="text-gray-600 text-sm mb-6">
              What you notice in the world. Signs, synchronicities, and meaningful coincidences.
            </p>
            <ComparisonTable
              optionA={universeParsed.optionA.name}
              optionB={universeParsed.optionB.name}
              itemsA={universeParsed.optionA.items}
              itemsB={universeParsed.optionB.items}
              iconA={decision.option_a_icon}
              iconB={decision.option_b_icon}
            />
          </div>
        )}

        {/* Vibe Section */}
        {decision.vibe_input && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Vibe</h2>
            </div>
            <p className="text-gray-600 text-sm mb-6">
              Body wisdom. Your gut feelings, intuition, and how each option feels in your body.
            </p>
            <ComparisonTable
              optionA={vibeParsed.optionA.name}
              optionB={vibeParsed.optionB.name}
              itemsA={vibeParsed.optionA.items}
              itemsB={vibeParsed.optionB.items}
              iconA={decision.option_a_icon}
              iconB={decision.option_b_icon}
            />
          </div>
        )}

        {/* Recommendation / Synthesis */}
        <div className="bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-2xl shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Synthesis</h2>
          <div className="text-lg text-gray-700 leading-relaxed space-y-3">
            {renderSynthesis(decision.ai_recommendation)}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TestCaseViewer;
